// AvatarBuilderPage — for users who don't want to upload their own photo.
//
// Collects attributes (gender presentation, age range, height, build, skin tone,
// hair length + color) plus sizes (tops / bottoms / dresses), POSTs to
// /auth/profile/generate-avatar/, which calls Replicate Flux Schnell and
// stores the result as the user's bodyPhotoUrl. Sizes persist to the same
// localStorage key that OnboardingSizingPage already writes (`trimie_sizing`),
// so the avatar builder doubles as a sizing form.

import { useState, useRef, useEffect } from 'react';
import api from './api';
import Header from './components/Header';

const SIZING_KEY = 'trimie_sizing';

// All allowed values mirror the whitelists in backend GenerateAvatarAPI._build_prompt.
// Keep these in sync — values get spliced into the Flux prompt server-side.
const GENDERS = [
  { value: 'female',     label: 'Female' },
  { value: 'male',       label: 'Male' },
  { value: 'non-binary', label: 'Non-binary' },
];
const AGE_RANGES = [
  { value: '18-25', label: '18–25' },
  { value: '25-35', label: '25–35' },
  { value: '35-45', label: '35–45' },
  { value: '45+',   label: '45+' },
];
const BUILDS = [
  { value: 'slim',     label: 'Slim' },
  { value: 'average',  label: 'Average' },
  { value: 'curvy',    label: 'Curvy' },
  { value: 'athletic', label: 'Athletic' },
];
const SKIN_TONES = [
  { value: 'fair',   swatch: '#F5DCC1' },
  { value: 'light',  swatch: '#E5BC95' },
  { value: 'medium', swatch: '#C68A66' },
  { value: 'tan',    swatch: '#8A553A' },
  { value: 'deep',   swatch: '#4A2A1C' },
];
const HAIR_LENGTHS = [
  { value: 'short',  label: 'Short' },
  { value: 'medium', label: 'Medium' },
  { value: 'long',   label: 'Long' },
];
const HAIR_COLORS = [
  { value: 'black',  label: 'Black' },
  { value: 'brown',  label: 'Brown' },
  { value: 'blonde', label: 'Blonde' },
  { value: 'red',    label: 'Red' },
  { value: 'gray',   label: 'Gray' },
  { value: 'other',  label: 'Other' },
];
const SIZES = ['XS', 'S', 'M', 'L', 'XL'];
const SIZE_ROWS = [
  { key: 'tops',     label: 'Tops' },
  { key: 'bottoms',  label: 'Bottoms' },
  { key: 'dresses',  label: 'Dresses' },
];

const loadSizing = () => {
  try { return JSON.parse(localStorage.getItem(SIZING_KEY) || '{}'); }
  catch { return {}; }
};

export default function AvatarBuilderPage({ onNav, onUserUpdate, user }) {
  const [gender, setGender]         = useState('female');
  const [ageRange, setAgeRange]     = useState('25-35');
  const [height, setHeight]         = useState('');
  const [build, setBuild]           = useState('average');
  const [skinTone, setSkinTone]     = useState('medium');
  const [hairLength, setHairLength] = useState('medium');
  const [hairColor, setHairColor]   = useState('brown');
  const [sizes, setSizes]           = useState(loadSizing);

  const [headshotFile, setHeadshotFile]       = useState(null);
  const [headshotPreview, setHeadshotPreview] = useState(null);
  const headshotInputRef = useRef(null);

  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [previewUrl, setPreviewUrl]   = useState(null);
  const [showResult, setShowResult]   = useState(false);  // controls full-screen modal
  const previewRef                    = useRef(null);

  // Auto-open the modal when a new preview lands. Tested users were scrolling
  // past the inline preview and missing the result entirely — a modal makes
  // it unmissable.
  useEffect(() => {
    if (previewUrl) setShowResult(true);
  }, [previewUrl]);

  const applyHeadshot = (f) => {
    if (!f) return;
    setHeadshotFile(f);
    setHeadshotPreview(URL.createObjectURL(f));
  };

  const clearHeadshot = () => {
    setHeadshotFile(null);
    setHeadshotPreview(null);
    if (headshotInputRef.current) headshotInputRef.current.value = '';
  };

  const persistSizes = () => {
    try { localStorage.setItem(SIZING_KEY, JSON.stringify(sizes)); }
    catch { /* quota — fine */ }
  };

  const generate = async () => {
    setLoading(true);
    setError(null);
    setPreviewUrl(null);
    persistSizes();
    try {
      // Multipart when a headshot is provided so the backend can route to
      // PhotoMaker for identity-preserved generation. Otherwise JSON works
      // fine and goes through Flux Schnell.
      let res;
      if (headshotFile) {
        const form = new FormData();
        form.append('gender_presentation', gender);
        form.append('age_range', ageRange);
        form.append('height', height);
        form.append('build', build);
        form.append('skin_tone', skinTone);
        form.append('hair_length', hairLength);
        form.append('hair_color', hairColor);
        form.append('headshot', headshotFile);
        res = await api.post('/auth/profile/generate-avatar/', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        res = await api.post('/auth/profile/generate-avatar/', {
          gender_presentation: gender,
          age_range: ageRange,
          height,
          build,
          skin_tone: skinTone,
          hair_length: hairLength,
          hair_color: hairColor,
        });
      }
      const newUrl = res.data?.data?.bodyPhotoUrl;
      if (!newUrl) throw new Error('No avatar URL returned.');
      setPreviewUrl(newUrl);
      if (onUserUpdate) onUserUpdate({ ...user, bodyPhotoUrl: newUrl });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Generation failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="screen">
      <Header title="Build avatar" onBack={() => onNav('me')} />

      <div className="avatar-body">
        <p className="lead avatar-lead">
          Don't want to upload a photo? Tell us a few things and we'll generate a
          body-photo stand-in for try-on. Add a headshot for a closer likeness.
        </p>

        {/* Preview slot — fills with result on success. Scrolls itself into
            view when previewUrl flips. */}
        <div className="avatar-preview" ref={previewRef}>
          {previewUrl ? (
            <img src={previewUrl} alt="" />
          ) : loading ? (
            <div className="avatar-preview-loading">
              <span className="spinner" style={{ color: 'var(--t-purple)' }} />
              <span className="avatar-preview-label">
                {headshotFile ? 'Building your likeness…' : 'Generating…'}
              </span>
              <span className="avatar-preview-sub">
                {headshotFile ? '~20–40 seconds' : '~10 seconds'}
              </span>
            </div>
          ) : (
            <div className="avatar-preview-placeholder">
              <svg width="44" height="56" viewBox="0 0 100 200" fill="none" stroke="var(--t-purple-soft)" strokeWidth="3">
                <circle cx="50" cy="36" r="18" />
                <path d="M22 174v-50c0-18 12-30 28-30s28 12 28 30v50" strokeLinecap="round" />
              </svg>
              <span>Preview appears here</span>
            </div>
          )}
        </div>

        {/* Primary actions live right under the preview so the user doesn't
            have to scroll past the long form to keep working with the result. */}
        <div className="avatar-actions">
          <button
            className="btn-purple btn-lg"
            type="button"
            onClick={generate}
            disabled={loading}
          >
            {loading
              ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}><span className="spinner" />Generating…</span>
              : previewUrl ? 'Regenerate' : 'Generate avatar'}
          </button>
          {previewUrl && (
            <button
              className="btn-outline btn-lg"
              type="button"
              onClick={() => { persistSizes(); onNav('me'); }}
            >
              Use this as my body photo
            </button>
          )}
        </div>

        {error && <div className="alert error">{error}</div>}

        {/* ── Optional headshot for likeness ───────────────────────────── */}
        <Field label="Headshot (optional, for likeness)">
          <div className="avatar-headshot-row">
            {headshotPreview ? (
              <div className="avatar-headshot-preview">
                <img src={headshotPreview} alt="" />
                <button
                  type="button"
                  className="avatar-headshot-clear"
                  onClick={clearHeadshot}
                  aria-label="Remove headshot"
                >×</button>
              </div>
            ) : (
              <button
                type="button"
                className="avatar-headshot-pick"
                onClick={() => headshotInputRef.current?.click()}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="9" r="3.5" />
                  <path d="M5 20c0-4 3.5-6 7-6s7 2 7 6" />
                </svg>
                <span>Upload headshot</span>
              </button>
            )}
            <div className="avatar-headshot-hint">
              {headshotFile
                ? "We'll use this for a likeness-preserved avatar. Takes 20–40s."
                : "Skip to generate a generic body photo (~10s)."}
            </div>
          </div>
          <input
            ref={headshotInputRef}
            type="file"
            accept="image/jpeg,image/png"
            hidden
            onChange={(e) => applyHeadshot(e.target.files[0])}
          />
        </Field>

        {/* ── Identity ─────────────────────────────────────────────────── */}
        <Field label="Gender presentation">
          <ChipRow options={GENDERS} value={gender} onChange={setGender} />
        </Field>

        <Field label="Age range">
          <ChipRow options={AGE_RANGES} value={ageRange} onChange={setAgeRange} />
        </Field>

        <Field label="Height (optional)">
          <input
            type="text"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            placeholder={`e.g. 5'7" or 170cm`}
            maxLength={24}
          />
        </Field>

        <Field label="Build">
          <ChipRow options={BUILDS} value={build} onChange={setBuild} />
        </Field>

        <Field label="Skin tone">
          <div className="avatar-swatches">
            {SKIN_TONES.map((s) => (
              <button
                key={s.value}
                type="button"
                className={`avatar-swatch${skinTone === s.value ? ' active' : ''}`}
                style={{ background: s.swatch }}
                aria-label={s.value}
                aria-pressed={skinTone === s.value}
                onClick={() => setSkinTone(s.value)}
              />
            ))}
          </div>
        </Field>

        <Field label="Hair length">
          <ChipRow options={HAIR_LENGTHS} value={hairLength} onChange={setHairLength} />
        </Field>

        <Field label="Hair color">
          <ChipRow options={HAIR_COLORS} value={hairColor} onChange={setHairColor} />
        </Field>

        {/* ── Sizes — written to localStorage, shared with onboarding ─── */}
        <Field label="Your usual sizes">
          <div className="avatar-sizes">
            {SIZE_ROWS.map(({ key, label }) => (
              <div key={key} className="avatar-size-row">
                <div className="avatar-size-row-label">{label}</div>
                <div className="onb-size-row">
                  {SIZES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`onb-size-cell${sizes[key] === s ? ' active' : ''}`}
                      onClick={() => setSizes((p) => ({ ...p, [key]: s }))}
                      aria-pressed={sizes[key] === s}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Field>

      </div>

      {/* Result modal — pops over the page on success. Click outside or hit
          Regenerate to dismiss. Made tall + scrollable so the body photo
          shows head-to-feet without cropping. */}
      {showResult && previewUrl && (
        <div className="avatar-result-backdrop" onClick={() => setShowResult(false)}>
          <div className="avatar-result-modal" onClick={(e) => e.stopPropagation()}>
            <div className="avatar-result-image">
              <img src={previewUrl} alt="Generated avatar" />
            </div>
            <div className="avatar-result-actions">
              <button
                className="btn-purple btn-lg"
                type="button"
                onClick={() => { persistSizes(); onNav('me'); }}
              >
                Use this as my body photo
              </button>
              <button
                className="btn-outline btn-lg"
                type="button"
                onClick={() => { setShowResult(false); generate(); }}
                disabled={loading}
              >
                Regenerate
              </button>
              <button
                className="link-inline"
                type="button"
                onClick={() => setShowResult(false)}
                style={{ marginTop: 4 }}
              >
                Keep tweaking the form
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="avatar-field">
      <span className="form-label">{label}</span>
      {children}
    </div>
  );
}

function ChipRow({ options, value, onChange }) {
  return (
    <div className="cat-chip-row">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`cat-chip${value === o.value ? ' active' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
